import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { projectsApi } from "@/api/projects";
import { ApiError } from "@/api/client";

export const Route = createFileRoute("/app/projects/$pid/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: project } = useQuery({ queryKey: ["project", pid], queryFn: () => projectsApi.get(pid) });

  const [name, setName] = useState(project?.name ?? "");
  if (project && name === "" && project.name) setName(project.name); // hydrate once

  const [renameErr, setRenameErr] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: () => projectsApi.rename(pid, name.trim()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", pid] }),
    onError: (e) => setRenameErr(e instanceof ApiError ? e.message : "Failed"),
  });

  const remove = useMutation({
    mutationFn: () => projectsApi.remove(pid),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate({ to: "/app", replace: true });
    },
  });

  return (
    <div className="space-y-8">
      <section className="card max-w-2xl">
        <h2 className="text-lg font-medium">Rename</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); setRenameErr(null); if (name.trim()) rename.mutate(); }}
          className="mt-4 space-y-3"
        >
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          {renameErr && <p className="text-sm text-red-600">{renameErr}</p>}
          <button type="submit" className="btn-primary" disabled={rename.isPending || !name.trim() || name.trim() === project?.name}>
            {rename.isPending ? "Saving…" : "Save"}
          </button>
        </form>
      </section>

      <section className="card max-w-2xl border-red-200">
        <h2 className="text-lg font-medium text-red-700">Delete project</h2>
        <p className="mt-1 text-sm text-ink/65">
          Permanently removes the project and all its API keys, members, and historical usage. This cannot be undone.
        </p>
        <button
          onClick={() => {
            if (confirm(`Type the project name to confirm.\n\nThis will permanently delete "${project?.name}".`)) {
              const v = prompt("Type the project name:");
              if (v === project?.name) remove.mutate();
              else if (v) alert("Name didn't match — not deleted.");
            }
          }}
          className="btn-danger mt-4"
          disabled={remove.isPending}
        >
          {remove.isPending ? "Deleting…" : "Delete project"}
        </button>
      </section>
    </div>
  );
}
